import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Zap,
  FileText,
  MessageSquare,
} from 'lucide-react';
import api from '../../api';

interface DocumentContext {
  id?: number | string;
  name?: string;
  type?: string;
  char_length?: number;
  preview?: string;
}

interface AILogEntry {
  id: string;
  timestamp: string;
  user_email: string;
  user_name: string;
  status: string;
  additional_data: {
    feature?: string;
    model?: string;
    prompt_summary?: string;
    response_summary?: string;
    latency_ms?: number;
    token_usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
    error_message?: string;
    documents_context?: string | DocumentContext[] | DocumentContext | null;
    conversation_turn?: number | null;
  } | null;
}

const FEATURE_LABELS: Record<string, string> = {
  evidence_assistant:                 'Evidence Assistant',
  change_impact:                      'Change Impact',
  questionnaire_autofill:             'Questionnaire Autofill',
  ai_assistant:                       'AI Assistant',
  policy_search:                      'Policy Search',
  ai_council_member:                  'AI Council (Member)',
  ai_council_president:               'AI Council (President)',
  ai_council_chat:                    'AI Council (Follow-up)',
  ai_council_multi_angle:             'AI Council (Multi-Angle)',
  ai_council_implementation_review:   'AI Council (Impl. Review)',
  ai_council_generate_prompt:         'AI Council (Prompt Gen)',
  jarvis_intelligence_chat:           'Jarvis Intelligence',
  risk_assessment:                    'Risk Assessment',
  process_risk_analysis:              'Process Risk Analysis',
  process_risk_analysis_pass1:        'Process Risk (Pass 1)',
  process_risk_analysis_pass2:        'Process Risk (Pass 2)',
  process_risk_identify:              'Process Risk (Identify)',
  process_risk_scoring:               'Process Risk (Scoring)',
  risk_assessment_semantic:           'Risk Assessment (Semantic)',
  sop_generation:                     'SOP Generation',
  sop_tool_comparison:                'SOP Tool Comparison',
  friction_estimation:                'Friction Estimation',
  jarvis_intelligence_analysis:       'Jarvis Intelligence (Analysis)',
  jarvis_intelligence_chat_followup:  'Jarvis Intelligence (Follow-up)',
  smart_data_analyst_plan:            'Smart Data Analyst (Plan)',
  smart_data_analyst_codegen:         'Smart Data Analyst (Code)',
  smart_data_analyst_interpret:       'Smart Data Analyst (Interpret)',
  pe_due_diligence:                   'PE Due Diligence',
  pe_financial_analysis:              'PE Financial Analysis',
  pe_ic_memo:                         'PE IC Memo',
};

// Prefix match for dynamic council member features like "ai_council_member_claude-4-sonnet"
function getFeatureLabel(feature: string): string {
  if (FEATURE_LABELS[feature]) return FEATURE_LABELS[feature];
  if (feature.startsWith('ai_council_member_')) return `AI Council (${feature.replace('ai_council_member_', '')})`;
  return feature;
}

const FEATURE_COLORS: Record<string, string> = {
  evidence_assistant:                 'bg-purple-50 text-purple-700',
  change_impact:                      'bg-orange-50 text-orange-700',
  questionnaire_autofill:             'bg-cyan-50 text-cyan-700',
  ai_assistant:                       'bg-blue-50 text-blue-700',
  policy_search:                      'bg-green-50 text-green-700',
  ai_council_member:                  'bg-indigo-50 text-indigo-700',
  ai_council_president:               'bg-violet-50 text-violet-700',
  ai_council_chat:                    'bg-fuchsia-50 text-fuchsia-700',
  ai_council_multi_angle:             'bg-indigo-50 text-indigo-700',
  ai_council_implementation_review:   'bg-indigo-50 text-indigo-700',
  ai_council_generate_prompt:         'bg-indigo-50 text-indigo-700',
  jarvis_intelligence_chat:           'bg-sky-50 text-sky-700',
  risk_assessment:                    'bg-red-50 text-red-700',
  process_risk_analysis:              'bg-rose-50 text-rose-700',
  process_risk_analysis_pass1:        'bg-rose-50 text-rose-700',
  process_risk_analysis_pass2:        'bg-rose-50 text-rose-700',
  process_risk_identify:              'bg-rose-50 text-rose-700',
  process_risk_scoring:               'bg-rose-50 text-rose-700',
  risk_assessment_semantic:           'bg-red-50 text-red-700',
  sop_generation:                     'bg-teal-50 text-teal-700',
  sop_tool_comparison:                'bg-teal-50 text-teal-700',
  friction_estimation:                'bg-amber-50 text-amber-700',
  jarvis_intelligence_analysis:       'bg-sky-50 text-sky-700',
  jarvis_intelligence_chat_followup:  'bg-sky-50 text-sky-700',
  smart_data_analyst_plan:            'bg-emerald-50 text-emerald-700',
  smart_data_analyst_codegen:         'bg-emerald-50 text-emerald-700',
  smart_data_analyst_interpret:       'bg-emerald-50 text-emerald-700',
  pe_due_diligence:                   'bg-yellow-50 text-yellow-700',
  pe_financial_analysis:              'bg-yellow-50 text-yellow-700',
  pe_ic_memo:                         'bg-yellow-50 text-yellow-700',
};

function getFeatureColor(feature: string): string {
  if (FEATURE_COLORS[feature]) return FEATURE_COLORS[feature];
  if (feature.startsWith('ai_council_member_')) return 'bg-indigo-50 text-indigo-700';
  return 'bg-gray-100 text-gray-600';
}

const PAGE_SIZE = 25;

/** Parse documents_context from whatever shape it was stored as */
function parseDocumentsContext(raw: string | DocumentContext[] | DocumentContext | null | undefined): DocumentContext[] | null {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === 'object') return [parsed];
    return null;
  } catch {
    return null;
  }
}

const AIActivityLog: React.FC = () => {
  const [logs, setLogs] = useState<AILogEntry[]>([]);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [featureFilter, setFeatureFilter] = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        actionCategory: 'AI_QUERY',
        page,
        limit: PAGE_SIZE,
        sortBy: 'event_timestamp',
        sortOrder: 'DESC',
      };
      if (featureFilter) params.action = `ai_query_${featureFilter}`;
      if (statusFilter)  params.status  = statusFilter;
      if (search)        params.clientName = search;

      const res = await api.get('/api/audit/logs', { params });
      const data = res.data;
      setLogs(data.auditLogs || []);
      setTotal(data.pagination?.totalRecords || 0);
      setTotalPages(data.pagination?.totalPages || 0);
    } catch (err) {
      console.error('Failed to load AI activity logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, featureFilter, statusFilter, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const exportCSV = () => {
    const rows = [
      ['Timestamp','User','Feature','Model','Status','Latency(ms)','Conv. Turn','Documents','Prompt','Response'],
      ...logs.map(l => {
        const d = l.additional_data || {};
        const docs = parseDocumentsContext(d.documents_context);
        const docsStr = docs
          ? docs.map(dc => dc.name || dc.id || dc.type || 'doc').join(' | ')
          : '';
        return [
          new Date(l.timestamp).toISOString(),
          l.user_email,
          getFeatureLabel(d.feature || ''),
          d.model || '',
          l.status,
          d.latency_ms ?? '',
          d.conversation_turn ?? '',
          docsStr,
          (d.prompt_summary   || '').replace(/,/g, ';'),
          (d.response_summary || '').replace(/,/g, ';'),
        ];
      }),
    ];
    const csv  = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `ai_activity_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const StatusBadge = ({ status }: { status: string }) => {
    if (status === 'SUCCESS')  return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50  text-green-700  border border-green-200  rounded text-xs font-medium"><CheckCircle className="w-3 h-3"/>Success</span>;
    if (status === 'FALLBACK') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-xs font-medium"><AlertCircle className="w-3 h-3"/>Fallback</span>;
    if (status === 'ERROR')    return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50    text-red-700    border border-red-200    rounded text-xs font-medium"><XCircle    className="w-3 h-3"/>Error</span>;
    return <span className="text-xs text-gray-500">{status}</span>;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">AI Activity Log</h2>
          <span className="text-sm text-gray-500">({total.toLocaleString()} total queries)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCSV}
            disabled={logs.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
          >
            <Download className="w-4 h-4" /><span>Export CSV</span>
          </button>
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center border border-gray-300 rounded-md px-3 py-1.5 bg-white flex-1 min-w-48">
          <Search className="w-4 h-4 text-gray-400 mr-2 shrink-0" />
          <input
            type="text"
            placeholder="Search by user or client..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="text-sm outline-none flex-1 bg-transparent"
          />
        </div>
        <select
          value={featureFilter}
          onChange={e => { setFeatureFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white"
        >
          <option value="">All Features</option>
          {Object.entries(FEATURE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white"
        >
          <option value="">All Statuses</option>
          <option value="SUCCESS">Success</option>
          <option value="FALLBACK">Fallback</option>
          <option value="ERROR">Error</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />Loading AI activity logs...
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Bot className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm font-medium">No AI activity logs found</p>
            <p className="text-xs mt-1">Queries will appear here once users use AI features</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Timestamp</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Feature</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Model</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Latency</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Documents</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Prompt</th>
                  <th className="w-8 px-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map(log => {
                  const isExpanded = expandedId === log.id;
                  const d = log.additional_data || {};
                  const docs = parseDocumentsContext(d.documents_context);
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => setExpandedId(prev => prev === log.id ? null : log.id)}
                      >
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-xs">
                            <Clock className="w-3 h-3" />
                            {new Date(log.timestamp).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs font-medium text-gray-800">{log.user_name}</div>
                          <div className="text-xs text-gray-400">{log.user_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          {d.feature
                            ? <span className={`px-2 py-0.5 rounded text-xs font-medium ${getFeatureColor(d.feature)}`}>
                                {getFeatureLabel(d.feature)}
                              </span>
                            : <span className="text-gray-400 text-xs">—</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-mono text-xs">{d.model || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={log.status} /></td>
                        <td className="px-4 py-3">
                          {d.latency_ms != null
                            ? <div className="flex items-center gap-1 text-xs text-gray-500"><Zap className="w-3 h-3" />{d.latency_ms}ms</div>
                            : <span className="text-gray-400 text-xs">—</span>
                          }
                        </td>
                        <td className="px-4 py-3">
                          {docs && docs.length > 0 ? (
                            <div className="flex items-center gap-1 text-xs text-blue-600">
                              <FileText className="w-3 h-3 shrink-0" />
                              <span className="truncate max-w-[120px]">
                                {docs.length === 1
                                  ? (docs[0].name || docs[0].type || `Doc ${docs[0].id}`)
                                  : `${docs.length} docs`}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs max-w-xs">
                          <span className="block truncate">{d.prompt_summary || '—'}</span>
                        </td>
                        <td className="px-2 py-3 text-gray-400">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-blue-50/40">
                          <td colSpan={9} className="px-6 py-4 border-b border-blue-100">
                            <div className="space-y-4 text-sm">

                              {/* Documents context */}
                              {docs && docs.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                                    <FileText className="w-3 h-3" /> Documents / Context Passed to AI
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {docs.map((doc, i) => (
                                      <div key={i} className="bg-white border border-blue-200 rounded px-3 py-1.5 text-xs text-blue-800">
                                        {doc.name && <span className="font-medium">{doc.name}</span>}
                                        {doc.id && !doc.name && <span className="font-medium">ID: {doc.id}</span>}
                                        {doc.type && <span className="ml-1 text-blue-400">({doc.type})</span>}
                                        {doc.char_length && <span className="ml-1 text-gray-400">{(doc.char_length / 1000).toFixed(1)}k chars</span>}
                                        {doc.preview && (
                                          <div className="mt-1 text-gray-500 italic truncate max-w-xs">{doc.preview}</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Conversation turn */}
                              {d.conversation_turn != null && (
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <MessageSquare className="w-3 h-3" />
                                  <span>Conversation turn: <strong>{d.conversation_turn}</strong></span>
                                </div>
                              )}

                              {/* Prompt + Response side by side */}
                              <div className="grid grid-cols-2 gap-6">
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Prompt</p>
                                  <p className="text-xs text-gray-700 whitespace-pre-wrap break-words bg-white rounded p-3 border border-gray-200 max-h-40 overflow-y-auto">
                                    {d.prompt_summary || '(none)'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Response</p>
                                  <p className="text-xs text-gray-700 whitespace-pre-wrap break-words bg-white rounded p-3 border border-gray-200 max-h-40 overflow-y-auto">
                                    {d.response_summary
                                      ? d.response_summary
                                      : d.error_message
                                      ? `Error: ${d.error_message}`
                                      : '(none)'}
                                  </p>
                                </div>
                              </div>

                              {/* Token usage */}
                              {d.token_usage && (
                                <div className="flex gap-6 text-xs text-gray-500">
                                  <span>Prompt tokens: <strong>{d.token_usage.prompt_tokens ?? '—'}</strong></span>
                                  <span>Completion: <strong>{d.token_usage.completion_tokens ?? '—'}</strong></span>
                                  <span>Total: <strong>{d.token_usage.total_tokens ?? '—'}</strong></span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Page {page} of {totalPages} ({total.toLocaleString()} records)</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIActivityLog;
